import React, { useCallback, useEffect, useState } from "react";
import { AxiosError } from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "../utils/toast";
import electricianImage from "../assets/services/electrician.webp";
import plumberImage from "../assets/services/plumber.webp";
import painterImage from "../assets/services/painter.webp";
import carpenterImage from "../assets/services/carpenter.webp";
import appliancesRepairImage from "../assets/services/appliances-repair.webp";
import AdminWorkspaceNav from "../components/admin/AdminWorkspaceNav";
import { useAuth } from "../contexts/AuthContext";
import {
  createAdminServiceablePincode,
  deleteAdminServiceablePincode,
  fetchAdminServiceBookingSettings,
  fetchAdminServiceablePincodes,
  fetchAdminServiceRequests,
  updateAdminServiceRequestStatus,
  updateAdminServiceablePincode,
  updateAdminServiceBookingSettings,
} from "../services/adminService";
import {
  ServiceBookingSettings,
  ServiceRequest,
  ServiceablePincode,
  ServiceablePincodePayload,
  ServiceRequestStatus,
} from "../types/store";
import { useProcessing } from "../contexts/ProcessingContext";
import ActionSwitchPill from "../components/shared/ActionSwitchPill";
import PageSpinner from "../components/shared/PageSpinner";

const serviceImages: Record<string, string> = {
  electrician: electricianImage,
  plumber: plumberImage,
  carpenter: carpenterImage,
  painter: painterImage,
  "appliances-repair": appliancesRepairImage,
};

const emptyPincode: ServiceablePincodePayload = {
  pincode: "",
  label: "",
  active: true,
};

const extractErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof AxiosError) {
    return (error.response?.data as { message?: string } | undefined)?.message || fallback;
  }
  return fallback;
};

const AdminServicesPage: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { startProcessing, stopProcessing } = useProcessing();
  const [serviceRequests, setServiceRequests] = useState<ServiceRequest[]>([]);
  const [serviceablePincodes, setServiceablePincodes] = useState<ServiceablePincode[]>([]);
  const [serviceBookingSettings, setServiceBookingSettings] = useState<ServiceBookingSettings | null>(null);
  const [serviceBookingSettingsLoaded, setServiceBookingSettingsLoaded] = useState(false);
  const [serviceRequestDrafts, setServiceRequestDrafts] = useState<Record<number, ServiceRequestStatus>>({});
  const [editingPincodeId, setEditingPincodeId] = useState<number | null>(null);
  const [pincodeForm, setPincodeForm] = useState<ServiceablePincodePayload>(emptyPincode);
  const [savingPincode, setSavingPincode] = useState(false);
  const [savingServiceBookingSettings, setSavingServiceBookingSettings] = useState(false);
  const [savingServiceRequestId, setSavingServiceRequestId] = useState<number | null>(null);
  const [pageReady, setPageReady] = useState(false);
  const isBookingEnabled = serviceBookingSettings?.enableServiceBooking ?? true;

  const handleAdminRequestError = useCallback((error: unknown, fallback: string) => {
    if (error instanceof AxiosError && error.response?.status === 401) {
      toast.error("Your admin session has expired. Please log in again.");
      logout();
      navigate("/admin/login", {
        replace: true,
        state: { from: location, adminOnly: true },
      });
      return;
    }

    if (error instanceof AxiosError && error.response?.status === 403) {
      toast.error("You do not have permission to manage services.");
      return;
    }

    toast.error(extractErrorMessage(error, fallback));
  }, [location, logout, navigate]);

  const loadData = useCallback(async () => {
    const processingId = startProcessing({
      title: "Loading services",
      message: "Fetching serviceable pincodes and customer requests...",
    });
    const [pincodeResult, requestResult, bookingResult] = await Promise.allSettled([
      fetchAdminServiceablePincodes(),
      fetchAdminServiceRequests(),
      fetchAdminServiceBookingSettings(),
    ]);

    if (pincodeResult.status === "fulfilled") {
      setServiceablePincodes(pincodeResult.value);
    } else {
      handleAdminRequestError(pincodeResult.reason, "Unable to load serviceable pincodes.");
    }

    if (requestResult.status === "fulfilled") {
      setServiceRequests(requestResult.value);
      setServiceRequestDrafts(
        requestResult.value.reduce<Record<number, ServiceRequestStatus>>((acc, request) => {
          acc[request.id] = request.status;
          return acc;
        }, {}),
      );
    } else {
      handleAdminRequestError(requestResult.reason, "Unable to load service requests.");
    }

    if (bookingResult.status === "fulfilled") {
      setServiceBookingSettings(bookingResult.value);
      setServiceBookingSettingsLoaded(true);
    } else {
      handleAdminRequestError(bookingResult.reason, "Unable to load service booking settings.");
      setServiceBookingSettingsLoaded(true);
    }
    await Promise.all(
      Object.values(serviceImages).map(
        (image) =>
          new Promise<void>((resolve) => {
            const preview = new Image();
            preview.onload = () => resolve();
            preview.onerror = () => resolve();
            preview.src = image;
          }),
      ),
    );
    setPageReady(true);
    stopProcessing(processingId);
  }, [handleAdminRequestError, startProcessing, stopProcessing]);

  const refreshServiceRequests = useCallback(async () => {
    try {
      const requests = await fetchAdminServiceRequests();
      setServiceRequests(requests);
      setServiceRequestDrafts(
        requests.reduce<Record<number, ServiceRequestStatus>>((acc, request) => {
          acc[request.id] = request.status;
          return acc;
        }, {}),
      );
    } catch (error) {
      handleAdminRequestError(error, "Unable to refresh service requests.");
    }
  }, [handleAdminRequestError]);

  const handleServiceBookingToggle = async (enabled: boolean) => {
    const previous = serviceBookingSettings;
    setServiceBookingSettings((current) =>
      current
        ? { ...current, enableServiceBooking: enabled }
        : { id: 0, enableServiceBooking: enabled, createdAt: "", updatedAt: "" },
    );
    setSavingServiceBookingSettings(true);

    try {
      const updated = await updateAdminServiceBookingSettings({ enableServiceBooking: enabled });
      setServiceBookingSettings(updated);
      toast.success(enabled ? "Service bookings enabled." : "Service bookings hidden.");
    } catch (error) {
      setServiceBookingSettings(previous);
      handleAdminRequestError(error, "Unable to update the service booking setting.");
    } finally {
      setSavingServiceBookingSettings(false);
    }
  };

  const handleServiceRequestStatusUpdate = async (requestId: number) => {
    const nextStatus = serviceRequestDrafts[requestId];
    if (!nextStatus) {
      return;
    }

    setSavingServiceRequestId(requestId);
    try {
      const updated = await updateAdminServiceRequestStatus(requestId, nextStatus);
      setServiceRequests((current) =>
        current.map((request) => (request.id === requestId ? updated : request)),
      );
      setServiceRequestDrafts((current) => ({ ...current, [requestId]: updated.status }));
      toast.success("Service request status updated.");
    } catch (error) {
      handleAdminRequestError(error, "Unable to update service request status.");
    } finally {
      setSavingServiceRequestId(null);
    }
  };

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void refreshServiceRequests();
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [refreshServiceRequests]);

  if (!pageReady) {
    return (
      <section className="shell section page-section">
        <PageSpinner label="Loading services..." />
      </section>
    );
  }

  return (
    <section className="shell section page-section">
      <div className="page-header admin-page-header">
        <div>
          <span className="eyebrow">Admin workspace</span>
          <h1>Services management</h1>
        </div>
      </div>
      <AdminWorkspaceNav />

      <section className="store-card admin-panel admin-panel--full" style={{ marginBottom: 24 }}>
        <div className="admin-panel__heading">
          <div>
            <span className="eyebrow">Service visibility</span>
            <h2>Booking button control</h2>
          </div>
        </div>
        <ActionSwitchPill
          leftLabel="Coming soon"
          rightLabel="Book now"
          activeSide={isBookingEnabled ? "right" : "left"}
          caption={
            serviceBookingSettingsLoaded
              ? isBookingEnabled
                ? "Book now"
                : "Coming soon"
              : "Checking service status..."
          }
          tone="service"
          interactive={serviceBookingSettingsLoaded}
          disabled={savingServiceBookingSettings || !serviceBookingSettingsLoaded}
          onClick={() => void handleServiceBookingToggle(!isBookingEnabled)}
          ariaLabel="Toggle service booking availability"
        />
      </section>

      <div className="admin-orders-layout">
        <section className="store-card admin-panel admin-panel--full">
          <div className="admin-panel__heading">
            <div>
              <span className="eyebrow">Pincode service</span>
              <h2>{editingPincodeId ? "Edit serviceable pincode" : "Add serviceable pincode"}</h2>
            </div>
          </div>
          <form
            className="form-grid"
            onSubmit={async (event) => {
              event.preventDefault();
              const processingId = startProcessing({
                title: editingPincodeId ? "Updating pincode" : "Adding pincode",
                message: "Saving service coverage and refreshing the list...",
              });
              setSavingPincode(true);
              try {
                const payload = {
                  ...pincodeForm,
                  pincode: pincodeForm.pincode.replace(/\D/g, "").slice(0, 6),
                };
                if (editingPincodeId) {
                  await updateAdminServiceablePincode(editingPincodeId, payload);
                  toast.success("Pincode updated.");
                } else {
                  await createAdminServiceablePincode(payload);
                  toast.success("Pincode added.");
                }
                setPincodeForm(emptyPincode);
                setEditingPincodeId(null);
                await loadData();
              } catch (error) {
                handleAdminRequestError(error, "Unable to save pincode.");
              } finally {
                setSavingPincode(false);
                stopProcessing(processingId);
              }
            }}
          >
            <label>
              Pincode
              <input
                value={pincodeForm.pincode}
                inputMode="numeric"
                maxLength={6}
                onChange={(event) =>
                  setPincodeForm((current) => ({
                    ...current,
                    pincode: event.target.value.replace(/\D/g, "").slice(0, 6),
                  }))
                }
                required
              />
            </label>
            <label>
              Label
              <input
                value={pincodeForm.label || ""}
                onChange={(event) =>
                  setPincodeForm((current) => ({ ...current, label: event.target.value }))
                }
                placeholder="Hyderabad main service area"
              />
            </label>
            <label className="checkout-inline-check">
              <input
                type="checkbox"
                checked={pincodeForm.active}
                onChange={(event) =>
                  setPincodeForm((current) => ({ ...current, active: event.target.checked }))
                }
              />
              <span>Pincode is active</span>
            </label>
            <div className="admin-form-actions">
              <button className="button" type="submit" disabled={savingPincode}>
                {savingPincode ? (
                  <span className="button-loading">
                    <span className="button-loading__spinner" aria-hidden="true" />
                    Saving...
                  </span>
                ) : editingPincodeId ? (
                  "Update pincode"
                ) : (
                  "Add pincode"
                )}
              </button>
            </div>
          </form>
        </section>

        <section className="store-card admin-panel admin-panel--full">
          <div className="admin-panel__heading">
            <div>
              <span className="eyebrow">Home delivery coverage</span>
              <h2>Serviceable pincodes</h2>
            </div>
          </div>
          <div className="admin-table">
            <table>
              <thead>
                <tr>
                  <th>Pincode</th>
                  <th>Label</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {serviceablePincodes.map((pincode) => (
                  <tr key={pincode.id}>
                    <td>{pincode.pincode}</td>
                    <td>{pincode.label || "Service area"}</td>
                    <td>{pincode.active ? "Active" : "Inactive"}</td>
                    <td>
                      <div className="admin-table-actions">
                        <button
                          className="link-button"
                          type="button"
                          onClick={() => {
                            setEditingPincodeId(pincode.id);
                            setPincodeForm({
                              pincode: pincode.pincode,
                              label: pincode.label || "",
                              active: pincode.active,
                            });
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="link-button admin-danger-button"
                          type="button"
                          onClick={async () => {
                            try {
                              await deleteAdminServiceablePincode(pincode.id);
                              toast.success("Pincode deleted.");
                              if (editingPincodeId === pincode.id) {
                                setEditingPincodeId(null);
                                setPincodeForm(emptyPincode);
                              }
                              await loadData();
                            } catch (error) {
                              handleAdminRequestError(error, "Unable to delete pincode.");
                            }
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!serviceablePincodes.length ? (
                  <tr>
                    <td colSpan={4}>No serviceable pincodes added yet.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <section className="store-card admin-panel admin-panel--full" style={{ marginTop: 24 }}>
        <div className="admin-panel__heading">
          <div>
            <span className="eyebrow">Service requests</span>
            <h2>Customer service bookings</h2>
          </div>
          <button type="button" className="link-button" onClick={() => void refreshServiceRequests()}>
            Refresh
          </button>
        </div>
        <div className="admin-service-request-grid">
          {serviceRequests.map((request) => (
            <article key={request.id} className="admin-service-request-card">
              <div className="admin-service-request-card__header">
                <img
                  src={serviceImages[request.serviceKey] || serviceImages.electrician}
                  alt={request.serviceName}
                />
                <div>
                  <strong>{request.serviceName}</strong>
                  <span>{request.serviceKey}</span>
                </div>
                <span className={`order-card__badge order-card__badge--neutral admin-service-request-card__status admin-service-request-card__status--${request.status.toLowerCase()}`}>
                  {request.status.replaceAll("_", " ")}
                </span>
              </div>
              <div className="admin-service-request-card__grid">
                <div>
                  <span>Customer</span>
                  <strong>{request.customerName}</strong>
                </div>
                <div>
                  <span>Contact</span>
                  <strong>{request.phoneNumber}</strong>
                </div>
                <div>
                  <span>Address</span>
                  <strong>{request.address}</strong>
                  <p>{request.postalCode}</p>
                </div>
                <div>
                  <span>Schedule</span>
                  <strong>{request.preferredDate}</strong>
                  <p>{request.preferredTimeSlot}</p>
                </div>
                <div className="admin-service-request-card__full">
                  <span>Problem</span>
                  <p>{request.description}</p>
                </div>
                {request.problemImages.length ? (
                  <div className="admin-service-request-card__full">
                    <span>Images</span>
                    <div className="admin-image-preview-grid" style={{ marginTop: 12 }}>
                      {request.problemImages.map((image, index) => (
                        <div key={`${request.id}-${index}`} className="admin-image-preview-card">
                          <img src={image} alt={`${request.serviceName} ${index + 1}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="admin-service-request-card__footer">
                <label className="admin-service-request-card__status-field">
                  <span>Update status</span>
                  <select
                    value={serviceRequestDrafts[request.id] || request.status}
                    onChange={(event) =>
                      setServiceRequestDrafts((current) => ({
                        ...current,
                        [request.id]: event.target.value as ServiceRequestStatus,
                      }))
                    }
                  >
                    <option value="REQUESTED">Requested</option>
                    <option value="UNDER_REVIEW">Under review</option>
                    <option value="CONFIRMED">Confirmed</option>
                    <option value="SCHEDULED">Scheduled</option>
                    <option value="IN_PROGRESS">In progress</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </label>
                <button
                  type="button"
                  className="button"
                  disabled={savingServiceRequestId === request.id}
                  onClick={() => void handleServiceRequestStatusUpdate(request.id)}
                >
                  {savingServiceRequestId === request.id ? (
                    <span className="button-loading">
                      <span className="button-loading__spinner" aria-hidden="true" />
                      Saving...
                    </span>
                  ) : (
                    "Save status"
                  )}
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
};

export default AdminServicesPage;
